/**
 * SINGLE FLIGHT for reads — the machinery behind `apiRequest`'s GET coalescing.
 *
 * WHY THIS EXISTS
 *
 * A Business Hub page load was measured (task 72) issuing 34 requests, of which
 * 14 were a second, third, fourth or fifth copy of a request already on the
 * wire for the SAME url — `GET /workspaces/:id` five times, `/auth/me` four,
 * `/integrations` four. Nothing is fanned out per row or per column; the whole
 * surplus is independent widgets each mounting their own effect and each asking
 * for the same thing at the same instant. They do not need N answers, they need
 * one answer shared N ways.
 *
 * So: while an identical GET is on the wire, later callers join it instead of
 * opening a second one. `tryRefreshToken` in core.ts already does exactly this
 * for the refresh endpoint, and for the same reason.
 *
 * THIS IS NOT A CACHE. The entry is dropped the moment the request settles, so
 * the very next call after that goes to the server — there is no TTL, no
 * eviction policy, and no window in which a caller can be handed a value the
 * server has already superseded. It removes duplicates that are literally
 * simultaneous, and nothing else.
 *
 * It lives in its own module because core.ts is the request path and this is a
 * separable mechanism — and because core.ts reached 495 of its 500-line ceiling
 * with both in it.
 */

/**
 * How long an entry may stay joinable.
 *
 * `.then(release, release)` covers resolve and reject. It does not cover "never
 * settles", and `apiRequest` has neither a timeout nor an abort. A laptop waking
 * onto captive-portal wifi opens a TCP connection that neither answers nor
 * errors, and Chrome will sit on it for minutes. Without this sweep that entry
 * stays in the map for the whole window and every later caller of the url —
 * including the merchant clicking Retry — joins the dead promise instead of
 * opening a fresh connection, which is strictly worse than the independent
 * connections they each had before coalescing existed.
 *
 * So the PARKED ENTRY is bounded, not the request. The originating caller's own
 * promise is untouched and still resolves if the response ever arrives; all the
 * sweep does is stop NEW callers joining something that has stopped looking like
 * it will answer.
 *
 * The deliberate limit of that: a caller who joined at 14s is NOT rescued. They
 * are already bound to the never-settling promise and stay bound to it. Rescuing
 * them would mean re-issuing a request on their behalf, which is a retry policy,
 * and this module does not have one — `apiRequest` has no timeout for
 * un-joined callers either, so a joiner is no worse off than the originator it
 * joined. Fifteen seconds is chosen to make that window small: far past any
 * healthy response, far short of the minutes a hung socket costs.
 */
export const JOIN_WINDOW_MS = 15_000;

/**
 * One parked request. `join()` is a closure over THIS instance's flag, not a
 * shared key — see the note in `singleFlight`.
 */
interface ParkedGet {
  readonly promise: Promise<unknown>;
  readonly join: () => void;
}

const inFlightGets = new Map<string, ParkedGet>();

/**
 * The only `RequestInit` field a joinable read may carry.
 *
 * The rule is: if the caller customised the request AT ALL, the caller owns it.
 * Stated as an allowlist rather than a list of disqualifying fields on purpose —
 * an earlier version enumerated `body`, `signal`, `headers`, `credentials`,
 * `cache` and `mode`, which left `redirect`, `referrer`, `referrerPolicy`,
 * `integrity` and `keepalive` silently joinable, and would have left the next
 * field added to the spec joinable too. `redirect: 'manual'` alone turns a
 * followed response into an opaque one, and the key cannot see any of them.
 *
 * Inverted, the set cannot go stale: anything not named here disqualifies.
 */
const JOINABLE_OPTION_KEYS: ReadonlySet<string> = new Set(['method']);

/**
 * True when this request may share an answer with an identical one already in
 * flight. GET only — a POST/PUT/PATCH/DELETE is an action, and two of them are
 * two actions even when they look alike.
 */
export function isJoinable(options: RequestInit): boolean {
  if ((options.method ?? 'GET').toUpperCase() !== 'GET') return false;
  for (const field of Object.keys(options)) {
    if (!JOINABLE_OPTION_KEYS.has(field)) return false;
  }
  return true;
}

/**
 * Joining callers get a structured clone, not the shared object. Before
 * coalescing existed each caller parsed its own response body and so owned its
 * own object graph; cloning keeps that true, so a component that mutates what it
 * was given cannot corrupt a sibling's copy.
 */
function copyForJoiner<T>(value: T): T {
  if (typeof structuredClone !== 'function') return value;
  try {
    return structuredClone(value);
  } catch {
    // Non-cloneable payload (a Blob-ish or function-carrying body). Sharing the
    // reference is still no worse than the caller's own parse would have been.
    return value;
  }
}

/**
 * Run `start()` unless an identical call is already in flight under `key`, in
 * which case join that one.
 *
 * `key` must carry everything the response depends on that `start` closes over —
 * for API reads that is the auth token, the workspace and the endpoint, so a
 * workspace switch or a token refresh mid-flight can never hand one account's
 * response to another.
 */
export function singleFlight<T>(key: string, start: () => Promise<T>): Promise<T> {
  const existing = inFlightGets.get(key);
  if (existing) {
    existing.join();
    return existing.promise.then((value) => copyForJoiner(value as T));
  }

  const started = start();

  // WHETHER ANYONE JOINED IS PER-INSTANCE, NOT PER-KEY.
  //
  // This was a `Set<string>` keyed by `key`, and that was a race. An entry
  // swept at 15s with its request still pending settles later and runs
  // `release()` a SECOND time — by which point a new entry can hold the same
  // key with a live joiner. A key-scoped flag let the stale release wipe the
  // new entry's state, so its parked callback skipped the clone and handed its
  // joiner the raw shared object: exactly the corruption the clone exists to
  // prevent. A boolean in this closure is unreachable from any other instance's
  // release, which is the property that makes it safe.
  let joined = false;

  // The snapshot joiners copy from, taken in a continuation registered HERE —
  // before the originating caller's own `await` resumes, since `start()`'s
  // promise is returned below and any `.then` the caller chains is necessarily
  // later. Otherwise an originator that mutates its payload synchronously would
  // have already changed the object by the time a joiner cloned it, and the
  // invariant would hold joiner->joiner but not originator->joiner.
  //
  // The clone is skipped when nobody joined, which is the common case: a joiner
  // can only find this entry while it is still in the map, and `release` removes
  // it before the originator's continuation runs.
  const parked: Promise<T> = started.then((value) => (joined ? copyForJoiner(value) : value));
  // Nothing may join a rejected snapshot, and an unobserved rejection here
  // would surface as an unhandled rejection. The originator (and any joiner)
  // still sees the real failure through their own chain.
  parked.catch(() => { /* observed by the originator and every joiner */ });

  const entry: ParkedGet = { promise: parked, join: () => { joined = true; } };

  const release = (): void => {
    clearTimeout(sweep); // runs after `sweep` is assigned — both callers are async
    // Identity, not presence: a superseded entry's late release must not evict
    // its replacement.
    if (inFlightGets.get(key) === entry) inFlightGets.delete(key);
  };
  const sweep = setTimeout(release, JOIN_WINDOW_MS);
  // Both settle paths, without turning a rejection into an unhandled one.
  started.then(release, release);

  inFlightGets.set(key, entry);
  return started;
}
